import React, { useState, useEffect } from "react";
import { Box } from "@material-ui/core";
import { useSelector } from "react-redux";
import { useSelectedTheme } from "../../../../../../Hooks";
import { MPITableHeaderData } from "./MPITableHeaderData";
import {
  CustomSelect,
  NoDataFound,
  CustomViewModeSwitcher,
  MPISummaryCardSkeleton,
  TableSkeleton,
  BasicTable,
  CustomTableHeader,
  CustomPagination,
} from "../../../../../../Components";
import {
  GetPropertyMPI,
  GetSummaryPropertyMPIData,
} from "../../../../../../Services/PropertyMPI/PropertMPI";
import SummaryCard from "./SummaryCard/SummaryCard";

// icons
import { FileCheck03Icon, FileIcon } from "../../../../../../assets/icons";

//  styles
import useStyles from "./styles";

const VIEW_MODES = {
  TABLE: "tableView",
  GRID: "cards",
};

function MPI({ propretyId }) {
  const styles = useStyles();
  const {
    theme: { palette },
  } = useSelectedTheme();
  const { isDarkMode } = useSelector((state) => state.theme);

  const [viewMode, setViewMode] = useState(VIEW_MODES.GRID);
  const [isLoading, setIsLoading] = useState(false);
  const [selectType, setSelectType] = useState(2);
  const [mpiData, setMpiData] = useState({
    result: [],
    totalCount: 0,
  });
  const [summaryData, setsummaryData] = useState({
    result: [],
    totalCount: 0,
  });
  const [cardsPagination, setCardsPagination] = useState({
    pageSize: 25,
    pageIndex: 1,
  });
  const [tablePagination, setTablePagination] = useState({
    pageSize: 25,
    pageIndex: 1,
  });
  const [inputValueCards, setInputValueCards] = useState(
    cardsPagination.pageIndex
  );
  const [inputValueTable, setInputValueTable] = useState(
    tablePagination.pageIndex
  );

  const MODES = [
    {
      value: "cards",
      icon: (
        <FileCheck03Icon
          width={20}
          height={20}
          fill={palette.button.secondary_fg}
        />
      ),
      label: "Summary",
    },
    {
      value: "tableView",
      icon: (
        <FileIcon width={20} height={20} fill={palette.button.secondary_fg} />
      ),
      label: "Details",
    },
  ];

  const getPropertyMPI = async () => {
    setIsLoading(true);

    try {
      const body = {
        propertyId: Number(propretyId),
        pageIndex: tablePagination.pageIndex,
        pageSize: tablePagination.pageSize,
        categoryIdentifier: selectType,
      };

      const res = await GetPropertyMPI(body);
      if (!(res && res.status && res.status !== 200)) {
        setMpiData({
          result: res?.result,
          totalCount: res?.totalCount,
        });
      }
    } catch (error) {
      console.error("Error fetching the property MPI data!", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getSummaryPropertyMPI = async () => {
    setIsLoading(true);

    try {
      const body = {
        propertyId: Number(propretyId),
        pageIndex: cardsPagination.pageIndex,
        pageSize: cardsPagination.pageSize,
        categoryIdentifier: selectType,
      };

      const res = await GetSummaryPropertyMPIData(body);
      if (!(res && res.status && res.status !== 200)) {
        setsummaryData({
          result: res?.result,
          totalCount: res?.totalCount,
        });
      }
    } catch (error) {
      console.error("Error fetching the property MPI data!", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onChangeViewMode = (value) => {
    setViewMode(value);
  };

  const handleSelectedType = async (newValue) => {
    setSelectType(newValue);
  };

  useEffect(() => {
    if (propretyId) {
      if (viewMode === VIEW_MODES.TABLE) {
        getPropertyMPI();
      } else {
        getSummaryPropertyMPI();
      }
    }
  }, [propretyId, selectType, viewMode]);

  return (
    <Box>
      <Box
        className={styles.switcherBox}
        display="flex"
        justifyContent="flex-end"
      >
        <Box className={styles.viewModeContainer}>
          <CustomSelect
            options={[
              { id: 2, name: "Sale" },
              { id: 1, name: "Lease" },
            ]}
            onValueChange={handleSelectedType}
            varient="defalut"
            disabled={isLoading}
            placeholder={"Select Type"}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            defaultValue={selectType}
            customSelectClasses={styles.customSelect}
          />
          <CustomViewModeSwitcher
            modes={MODES}
            selectedMode={viewMode}
            onChangeMode={onChangeViewMode}
            isLoading={isLoading}
          />
        </Box>
      </Box>
      {viewMode === VIEW_MODES.GRID && (
        <>
          <Box className={styles.cardsContainer}>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, index) => (
                <MPISummaryCardSkeleton index={index} />
              ))
            ) : summaryData.result.length > 0 ? (
              <SummaryCard summaryData={summaryData.result} />
            ) : (
              <NoDataFound title="Summary Cards" />
            )}
          </Box>
          <CustomPagination
            totalItems={summaryData?.totalCount}
            itemsPerPage={cardsPagination.pageSize}
            currentPage={cardsPagination.pageIndex}
            inputValue={inputValueCards}
            setInputValue={setInputValueCards}
            onPageChange={(page) =>
              setCardsPagination((prev) => ({ ...prev, pageIndex: page }))
            }
            onItemsPerPageChange={(items) =>
              setCardsPagination((prev) => ({ ...prev, pageSize: items }))
            }
            isLoading={isLoading}
          />
        </>
      )}
      {viewMode === VIEW_MODES.TABLE && (
        <>
          <Box>
            {isLoading ? (
              <TableSkeleton rowsNum={8} />
            ) : mpiData?.result?.length > 0 ? (
              <BasicTable
                rowHeight={84}
                rowsData={
                  mpiData?.result.map((item, idx) => {
                    return {
                      ...item,
                      id: idx,
                    };
                  }) || []
                }
                columns={MPITableHeaderData(isDarkMode)?.map((column) => ({
                  ...column,
                  renderHeader: (params) => (
                    <CustomTableHeader
                      params={params}
                      title={column.headerName}
                      isHiddenFilter={true}
                    />
                  ),
                }))}
              />
            ) : (
              <NoDataFound title="Details" />
            )}
          </Box>
          <CustomPagination
            totalItems={mpiData?.totalCount}
            itemsPerPage={tablePagination.pageSize}
            currentPage={tablePagination.pageIndex}
            inputValue={inputValueTable}
            setInputValue={setInputValueTable}
            onPageChange={(page) =>
              setTablePagination((prev) => ({ ...prev, pageIndex: page }))
            }
            onItemsPerPageChange={(items) =>
              setTablePagination((prev) => ({ ...prev, pageSize: items }))
            }
            isLoading={isLoading}
          />
        </>
      )}
    </Box>
  );
}

export default MPI;
